import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const RichDescription = ({ 
    description, 
    maxLength = 300, 
    showLabel = true, 
    labelText = "Description",
    className = "",
    textClassName = "",
    compact = false
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!description || description.trim().length === 0) {
        return null;
    }

    const shouldTruncate = description.length > maxLength;
    const displayText = shouldTruncate && !isExpanded 
        ? description.substring(0, maxLength) + '...' 
        : description;

    // Function to parse and format special content like mentions, hashtags, etc.
    const formatText = (text) => {
        // Split by line breaks to preserve formatting
        const lines = text.split('\n');
        
        return lines.map((line, lineIndex) => {
            if (line.trim() === '') {
                return <br key={lineIndex} />;
            }

            // Split the line into parts, preserving emojis and special formatting
            const parts = line.split(/(\s+)/).map((part, partIndex) => {
                // Handle @mentions
                if (part.startsWith('@') && part.length > 1) {
                    const username = part.slice(1).replace(/[^\w]/g, '');
                    return (
                        <span key={`${lineIndex}-${partIndex}`} className="text-blue-400 hover:text-blue-300 cursor-pointer">
                            @{username}
                        </span>
                    );
                }

                // Handle hashtags
                if (part.startsWith('#') && part.length > 1) {
                    return (
                        <span key={`${lineIndex}-${partIndex}`} className="text-purple-400 hover:text-purple-300 cursor-pointer">
                            {part}
                        </span>
                    );
                }

                // Handle URLs (basic detection)
                if (part.startsWith('http') || part.includes('.com') || part.includes('.org') || part.includes('.net')) {
                    return (
                        <a 
                            key={`${lineIndex}-${partIndex}`}
                            href={part.startsWith('http') ? part : `https://${part}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 underline"
                        >
                            {part}
                        </a>
                    );
                }

                // Regular text (includes emojis naturally)
                return part;
            });

            return (
                <div key={lineIndex} className={lineIndex > 0 ? 'mt-2' : ''}>
                    {parts}
                </div>
            );
        });
    };

    return (
        <div className={className}>
            {showLabel && !compact && (
                <h3 className="text-lg font-semibold text-gray-300 mb-2">{labelText}</h3>
            )}
            {showLabel && compact && (
                <span className="text-gray-400 block mb-1">{labelText}:</span>
            )}
            
            <div className={`rich-description ${textClassName}`}>
                <div className="text-gray-300 leading-relaxed whitespace-pre-wrap font-sans">
                    {formatText(displayText)}
                </div>
                
                {shouldTruncate && (
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center gap-2 mt-3 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium"
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUp className="w-4 h-4" />
                                Show less
                            </>
                        ) : (
                            <>
                                <ChevronDown className="w-4 h-4" />
                                Show more
                            </>
                        )}
                    </button>
                )}
            </div>

        </div>
    );
};

export default RichDescription;